import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Anchor, Button, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import AuthCard from '@/components/auth/AuthCard';
import FormMessage from '@/components/auth/FormMessage';
import PasswordField from '@/components/auth/PasswordField';
import { userManagementService } from '@/api/services/UserManagementService';
import { getStatus, isNetworkError } from '@/api/errors';
import { NO_SPACES_PATTERN, PASSWORD_MIN_LENGTH, isBlank } from '@/lib/validators';
import { ROUTES } from '@/routes/paths';

interface ResetValues {
  password: string;
  confirmPassword: string;
}

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [submitting, setSubmitting] = useState(false);
  // 'expired' показывает ссылку «запросить новую»; 'unknown' — просто сообщение.
  const [resetError, setResetError] = useState<'expired' | 'unknown' | null>(null);

  const form = useForm<ResetValues>({
    mode: 'uncontrolled',
    initialValues: { password: '', confirmPassword: '' },
    validate: {
      password: (value) =>
        isBlank(value)
          ? t('auth.validation.required')
          : value.length < PASSWORD_MIN_LENGTH
            ? t('auth.validation.passwordMin')
            : !NO_SPACES_PATTERN.test(value)
              ? t('auth.validation.passwordSpaces')
              : null,
      confirmPassword: (value, values) =>
        isBlank(value)
          ? t('auth.validation.required')
          : value !== values.password
            ? t('auth.validation.passwordMismatch')
            : null,
    },
    validateInputOnBlur: true,
  });

  async function handleSubmit(values: ResetValues): Promise<void> {
    if (!id) {
      setResetError('expired');
      return;
    }
    setSubmitting(true);
    setResetError(null);
    try {
      await userManagementService.changePassword(id, values.password);
      notifications.show({ color: 'lime', message: t('auth.resetPassword.success') });
      navigate(ROUTES.LOGIN, { replace: true });
    } catch (error) {
      form.setFieldValue('confirmPassword', '');
      if (getStatus(error) === 406) {
        setResetError('expired');
      } else if (isNetworkError(error)) {
        notifications.show({ color: 'red', message: t('auth.errors.generic') });
      } else {
        setResetError('unknown');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard title={t('auth.resetPassword.title')} description={t('auth.resetPassword.text')}>
      <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
        <Stack gap="md">
          {resetError ? (
            <FormMessage>
              <span>{resetError === 'expired' ? t('auth.resetPassword.errorExpired') : t('auth.resetPassword.errorUnknown')}</span>
              {resetError === 'expired' ? (
                <Anchor component={Link} to={ROUTES.FORGOT_PASSWORD} size="sm" c="brand.7" underline="always">
                  {t('auth.resetPassword.requestNew')}
                </Anchor>
              ) : null}
            </FormMessage>
          ) : null}

          <PasswordField
            label={t('auth.fields.newPassword')}
            autoComplete="new-password"
            withAsterisk
            key={form.key('password')}
            {...form.getInputProps('password')}
          />
          <PasswordField
            label={t('auth.fields.confirmPassword')}
            autoComplete="new-password"
            withAsterisk
            key={form.key('confirmPassword')}
            {...form.getInputProps('confirmPassword')}
          />
          <Button type="submit" loading={submitting} fullWidth mt="xs">
            {t('auth.resetPassword.submit')}
          </Button>
        </Stack>
      </form>
    </AuthCard>
  );
}
